import { useRef, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { PortalInput } from './PortalInput'
import { PASSWORD_RULES } from '@/lib/validators'

interface PasswordInputProps {
  id?: string
  value?: string
  onChange?: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  showCriteria?: boolean
  autoComplete?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

export function PasswordInput({
  id,
  value = '',
  onChange,
  onBlur,
  placeholder,
  showCriteria = false,
  autoComplete,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: PasswordInputProps) {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)

  const criteria = [
    { key: 'minLength', ok: value.length >= PASSWORD_RULES.minLength },
    { key: 'upper', ok: PASSWORD_RULES.uppercase.test(value) },
    { key: 'lower', ok: PASSWORD_RULES.lowercase.test(value) },
    { key: 'digit', ok: PASSWORD_RULES.digit.test(value) },
    { key: 'special', ok: PASSWORD_RULES.special.test(value) },
  ]

  return (
    <div>
      <div className="prod-password-wrap">
        <PortalInput
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          onBlur={onBlur}
          onChange={(e) => onChange?.(e.target.value)}
        />
        <button
          type="button"
          className="prod-password-toggle"
          onClick={() => setVisible((v) => !v)}
          aria-label={t(visible ? 'common.hidePassword' : 'common.showPassword')}
        >
          {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
      {showCriteria ? (
        <div className="prod-criteria">
          {criteria.map((c) => (
            <span
              key={c.key}
              className={`prod-criteria__item ${c.ok ? 'prod-criteria__item--ok' : ''}`}
            >
              <Check className="size-3" />
              {t(`validation.${c.key}`)}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}

interface OtpInputProps {
  length?: number
  value: string
  onChange: (value: string) => void
}

export function OtpInput({ length = 6, value, onChange }: OtpInputProps) {
  const { t } = useTranslation()
  const refs = useRef<(HTMLInputElement | null)[]>([])

  function handleChange(index: number, char: string) {
    const digit = char.replace(/\D/g, '').slice(-1)
    const chars = value.padEnd(length, ' ').split('')
    chars[index] = digit || ' '
    const next = chars.join('').trimEnd()
    onChange(next.replace(/\s/g, ''))
    if (digit && index < length - 1) {
      refs.current[index + 1]?.focus()
    }
  }

  function handleKeyDown(index: number, key: string) {
    if (key === 'Backspace' && !value[index] && index > 0) {
      refs.current[index - 1]?.focus()
    }
  }

  return (
    <div className="prod-otp">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className="prod-otp__box"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          value={value[i] ?? ''}
          aria-label={t('auth.otpDigit', { current: i + 1, total: length })}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e.key)}
        />
      ))}
    </div>
  )
}
